import { CHARACTER_REGEX_GLOBAL } from 'core/constants/constants';
import { CanvasBaseModel } from 'core/constants/enums/CanvasBaseModel.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { trim } from 'core/utils/utils';
import { AssetDataIdentifier } from '../services/AssetsService';

class GenerateRequest {
	constructor(settings) {
		this.modelsList = settings.modelsList;
		this.stylesList = settings.stylesList;
		this.selectedModels = settings.selectedModels;
		this.selectedStyles = settings.selectedStyles;
		this.selectedLoRas = settings.selectedLoRas;
		this.prompt = settings.prompt;
		this.negativePrompt = settings.negativePrompt;
		this.samplerInput = settings.samplerInput;
		this.samplingStepsInput = settings.samplingStepsInput;
		this.aIGuidanceInput = settings.aIGuidanceInput;
		this.batchSizeInput = settings.batchSizeInput;
		this.seedInput = settings.seedInput;
		this.aspectRatioLabel = settings.aspectRatioLabel;
		this.quality = settings.quality;
		this.controlNetOptions = settings.controlNetOptions;
		this.facelockOptions = settings.facelockOptions;
		this.changeItOptions = settings.changeItOptions;
		this.trueTouchOptions = settings.trueTouchOptions;
		this.character = settings.character;
		this.faceSwapOptions = settings.faceSwapOptions;
		this.lipSyncOptions = settings.lipSyncOptions;
		this.upscaleOptions = settings.upscaleOptions;
		this.videoAnyoneOptions = settings.videoAnyoneOptions;
		this.faceReplaceOptions = settings.faceReplaceOptions;
		this.textToVideoOption = settings.textToVideoOption;
		this.audioAssetId = settings.audioAssetId;
		this.generationType = settings.generationType;
		this.changeClothingOptions = settings.changeClothingOptions;
		this.objectPlacementOptions = settings.objectPlacementOptions;
		this.imageReferenceOptions = settings.imageReferenceOptions;
	}

	#updatedPrompt() {
		// Regex to match any username enclosed inside carat symbols
		return this.prompt.replace(CHARACTER_REGEX_GLOBAL, '^character^');
	}

	#getBaseModelFromModelsList(modelName) {
		const base = Object.values(this.modelsList).find(
			(model) => model.name === modelName || model.id === modelName,
		);
		return base?.baseModel;
	}

	#getBaseModelFromStylesList(styleName) {
		const base = Object.values(this.stylesList).find(
			(style) => style.name === styleName || style.id === styleName,
		);
		return base?.baseModelId;
	}

	#getControlNetOptions(baseModel) {
		if (this.controlNetOptions && Object.keys(this.controlNetOptions).length > 0) {
			return {
				input_image: this.controlNetOptions.inputImage,
				control_mode: this.controlNetOptions.controlMode,
				resize_mode: this.controlNetOptions.resizeMode,
				...(baseModel === CanvasBaseModel.SDXL && {
					module: this.controlNetOptions.sdxl.module,
					model: this.controlNetOptions.sdxl.model,
					name: this.controlNetOptions.sdxl.name,
				}),
				...(baseModel === CanvasBaseModel.SD15 && {
					module: this.controlNetOptions.nonsdxl.module,
					model: this.controlNetOptions.nonsdxl.model,
					name: this.controlNetOptions.nonsdxl.name,
				}),
				...(baseModel === CanvasBaseModel.Flux && {
					module: this.controlNetOptions.flux.module,
					model: this.controlNetOptions.flux.model,
					name: this.controlNetOptions.flux.name,
				}),
			};
		}
		return null;
	}

	#getFacelockOptions() {
		if (this.facelockOptions && Object.keys(this.facelockOptions).length > 0) {
			return {
				input_image: this.facelockOptions?.inputImage,
			};
		}
		return null;
	}

	#getStyleRequestBody() {
		const requestBody = [];
		this.selectedStyles.forEach((style) => {
			const requestBodyItem = {
				style_id: style,
				aspect_ratio: this.aspectRatioLabel,
				batch_size: this.batchSizeInput,
				prompt: {
					positive: this.#updatedPrompt(),
					negative: this.negativePrompt,
				},
				seed: !this.seedInput || Number.isNaN(this.seedInput) ? -1 : Number(this.seedInput),
			};

			const baseModel = this.#getBaseModelFromStylesList(style);
			const controlNet = this.#getControlNetOptions(baseModel);
			if (controlNet) {
				requestBodyItem.control_net = controlNet;
			}

			const facelock = this.#getFacelockOptions();
			if (this.character == null && facelock) {
				requestBodyItem.facelock = facelock;
			}
			if (this.character) {
				requestBodyItem.character = this.character;
			}

			requestBody.push(requestBodyItem);
		});
		return requestBody;
	}

	#getModelRequestBody() {
		const requestBody = [];
		this.selectedModels.forEach((model) => {
			const requestBodyItem = {
				model_id: model,
				aspect_ratio: this.aspectRatioLabel,
				batch_size: this.batchSizeInput,
				cfg_scale: this.aIGuidanceInput,
				prompt: {
					positive: this.#updatedPrompt(),
					negative: this.negativePrompt,
				},
				quality: this.quality,
				sampler: this.samplerInput,
				seed: !this.seedInput || Number.isNaN(this.seedInput) ? -1 : Number(this.seedInput),
				steps: this.samplingStepsInput,
			};

			const baseModel = this.#getBaseModelFromModelsList(model);
			const controlNet = this.#getControlNetOptions(baseModel);
			if (controlNet) {
				requestBodyItem.control_net = controlNet;
			}

			const facelock = this.#getFacelockOptions();
			if (this.character == null && facelock) {
				requestBodyItem.facelock = facelock;
			}

			if (this.selectedLoRas) {
				requestBodyItem.loras = this.selectedLoRas;
			}

			if (this.character) {
				requestBodyItem.character = this.character;
			}

			requestBody.push(requestBodyItem);
		});
		return requestBody;
	}

	#getChangeItRequestBody() {
		return [
			{
				segment: {
					input_image: this.changeItOptions.inputImage,
				},
				segment_prompt: {
					find: this.changeItOptions.whatToChange,
					replace: this.changeItOptions.changeTo,
				},
			},
		];
	}

	#getTrueTouchRequestBody() {
		return [
			{
				true_touch: {
					input_image: this.trueTouchOptions.input_image,
					enhance_strength: this.trueTouchOptions.enhance_strength,
					scale_factor: this.trueTouchOptions.scale_factor,
				},
			},
		];
	}

	#getUpscaleRequestBody() {
		return [
			{
				upscale: {
					image: this.upscaleOptions.image,
					scale_to: this.upscaleOptions.scale_to,
				},
			},
		];
	}

	#getTextToVideoRequestBody() {
		return [
			{
				character: this.character,
				aspect_ratio: this.aspectRatioLabel,
				txt2vid: {
					duration: this.textToVideoOption.duration,
					prompt: this.textToVideoOption.prompt.replace(CHARACTER_REGEX_GLOBAL, '^character^'),
				},
			},
		];
	}

	#getVideoAnyoneRequestBody() {
		return [
			{
				generation_type: this.videoAnyoneOptions.generationType,
				video_anyone: {
					provider: this.videoAnyoneOptions.videoAnyone.provider,
					model: this.videoAnyoneOptions.videoAnyone.model,
					image: this.videoAnyoneOptions.videoAnyone.image ?? '',
					end_frame: this.videoAnyoneOptions.videoAnyone.endFrame ?? '',
					prompt: this.videoAnyoneOptions.videoAnyone.prompt,
					duration: this.videoAnyoneOptions.videoAnyone.duration,
				},
			},
		];
	}

	#getFaceReplaceRequestBody() {
		return [
			{
				face_replace: {
					image: this.faceReplaceOptions.image ?? '',
					faces: this.faceReplaceOptions.faces ?? {},
				},
			},
		];
	}

	#getFaceSwapRequestBody() {
		const videoAssetId = this.faceSwapOptions?.faceSwapVideoData?.[AssetDataIdentifier.ID];
		const imageAssetId = this.faceSwapOptions?.faceSwapImageData?.[AssetDataIdentifier.ID];
		return [
			{
				face_swap: {
					vid_asset_id: trim(videoAssetId),
					img_asset_id: trim(imageAssetId),
					preference: this.faceSwapOptions.preference,
				},
			},
		];
	}

	#getLipSyncRequestBody() {
		const videoAssetId = this.lipSyncOptions?.lipSyncVideoData?.[AssetDataIdentifier.ID];
		const imageAssetId = this.lipSyncOptions?.lipSyncImageData?.[AssetDataIdentifier.ID];
		const audioAssetId = this.lipSyncOptions?.lipSyncAudioData?.[AssetDataIdentifier.ID];
		const voiceId = this.lipSyncOptions?.lipSyncVoiceData?.id;
		return [
			{
				lip_sync: {
					vid_asset_id: trim(videoAssetId),
					img_asset_id: trim(imageAssetId),
					...(voiceId
						? {
								voice_id: voiceId,
								script: this.lipSyncOptions.lipSyncScript,
							}
						: {}),
					...(audioAssetId
						? {
								aud_asset_id: trim(audioAssetId),
							}
						: {}),
				},
			},
		];
	}

	#getMusicVideoRequestBody() {
		return [
			{
				generation_type: this.generationType,
				...(this.character && { character: this.character }),
				prompt: {
					positive: this.prompt,
					negative: this.negativePrompt,
				},
				aspect_ratio: this.aspectRatioLabel,
				aud_asset_id: this.audioAssetId,
				checkpoint: true,
			},
		];
	}

	#getChangeClothingRequestBody() {
		return [
			{
				generation_type: this.changeClothingOptions.generationType,
				cloth_swap: {
					category: this.changeClothingOptions.clothSwap.category,
					garment_id: this.changeClothingOptions.clothSwap.garmentId,
					input_image_id: this.changeClothingOptions.clothSwap.inputImageId,
					generation_mode: this.changeClothingOptions.clothSwap.generationMode,
				},
			},
		];
	}

	#getObjectPlacementRequestBody() {
		return [
			{
				generation_type: this.objectPlacementOptions.generationType,
				object_placement: {
					generation_mode: this.objectPlacementOptions.objectPlacement.generationMode,
					object_id: this.objectPlacementOptions.objectPlacement.objectId,
					// foreground_object_id: this.objectPlacementOptions.objectPlacement.foregroundObjectId,
					background_image_id: this.objectPlacementOptions.objectPlacement.backgroundImageId,
					object_location_image_id:
						this.objectPlacementOptions.objectPlacement.objectLocationImageId,
					composite_id: this.objectPlacementOptions.objectPlacement.compositeId,
					reflection: this.objectPlacementOptions.objectPlacement.reflection,
					rotation_angle:
						this.objectPlacementOptions.objectPlacement.objectTransformValues.rotation,
					horizontal_flip: this.objectPlacementOptions.objectPlacement.objectTransformValues.flipY,
					vertical_flip: this.objectPlacementOptions.objectPlacement.objectTransformValues.flipX,
				},
			},
		];
	}

	#getImageReferenceRequestBody() {
		return [
			{
				generation_type: this.imageReferenceOptions.generationType,
				flux_image_to_image: {
					seed: this.seedInput,
					batch_size: this.batchSizeInput,
					reference_image: this.imageReferenceOptions.fluxImageToImage.referenceImage,
					prompt: this.#updatedPrompt(),
					facelock: this.imageReferenceOptions.fluxImageToImage.facelock,
					character: this.character,
					intensity: this.imageReferenceOptions.fluxImageToImage.intensity,
				},
			},
		];
	}

	getGenerateRequestBody() {
		if (this.generationType === GenerationType.MUSIC_VIDEO) {
			return this.#getMusicVideoRequestBody();
		}
		if (this.changeItOptions) {
			return this.#getChangeItRequestBody();
		}
		if (this.trueTouchOptions) {
			return this.#getTrueTouchRequestBody();
		}
		if (this.faceSwapOptions) {
			return this.#getFaceSwapRequestBody();
		}
		if (this.lipSyncOptions) {
			return this.#getLipSyncRequestBody();
		}
		if (this.upscaleOptions) {
			return this.#getUpscaleRequestBody();
		}
		if (this.videoAnyoneOptions) {
			return this.#getVideoAnyoneRequestBody();
		}
		if (this.faceReplaceOptions) {
			return this.#getFaceReplaceRequestBody();
		}
		if (this.textToVideoOption) {
			return this.#getTextToVideoRequestBody();
		}

		if (this.changeClothingOptions) {
			return this.#getChangeClothingRequestBody();
		}
		if (this.objectPlacementOptions) {
			return this.#getObjectPlacementRequestBody();
		}
		if (this.imageReferenceOptions) {
			return this.#getImageReferenceRequestBody();
		}
		let modelRequestBody = [];
		let styleRequestBody = [];
		if (this.selectedModels && this.selectedModels?.length !== 0) {
			modelRequestBody = this.#getModelRequestBody();
		}
		if (this.selectedStyles && this.selectedStyles?.length !== 0) {
			styleRequestBody = this.#getStyleRequestBody();
		}

		return [...modelRequestBody, ...styleRequestBody];
	}
}

export default GenerateRequest;
